import type { CreateSourceResponseItem } from "@/features/adp/services/source/source.types";

export function parseExcelSheetsFromTablePaths(
  tablePaths: string[] | null | undefined
): { sheetNames: string[]; sheetPaths: string[] } {
  const outNames: string[] = []
  const outPaths: string[] = []
  if (!Array.isArray(tablePaths)) {
    return { sheetNames: outNames, sheetPaths: outPaths }
  }
  for (const e of tablePaths) {
    if (typeof e !== 'string') continue
    if (!looksLikeSheetPath(e)) continue
    const parts = e.split('/')
    if (parts.length === 0) continue
    const last = parts[parts.length - 1] ?? ''
    const sheet = last.split('.')
    if (sheet.length > 0) {
      outPaths.push(e)
      outNames.push(sheet[0] ?? e)
    }
  }
  return { sheetNames: outNames, sheetPaths: outPaths }
}

function looksLikeSheetPath(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (trimmed.includes('/') || trimmed.includes('\\')) return true

  const fileName = trimmed.split(/[\\/]/).pop() ?? ''
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() ?? '' : ''
  const knownFileExtensions = new Set(['xlsx', 'xls', 'csv', 'tsv', 'json', 'parquet', 'txt', 'xml'])

  return knownFileExtensions.has(extension)
}


type SourceTablesPayload =
  | string[]
  | {
    sheetPaths?: string[];
    sheetNames?: string[];
    tables?: string[];
  }
  | undefined;

export type ExtractedSourceTables = {
  tables: string[];
  sheetPaths: string[];
  sheetNames: string[];
};

export function extractSourceTables(tablesPayload: SourceTablesPayload): ExtractedSourceTables {
  if (Array.isArray(tablesPayload)) {
    const normalized = tablesPayload.filter((value): value is string => typeof value === "string");
    const parsed = parseExcelSheetsFromTablePaths(normalized);
    return {
      tables: normalized,
      sheetPaths: parsed.sheetPaths,
      sheetNames: parsed.sheetNames,
    };
  }

  if (!tablesPayload || typeof tablesPayload !== "object") {
    return { tables: [], sheetPaths: [], sheetNames: [] };
  }

  const objectPayload = tablesPayload as {
    sheetPaths?: unknown;
    sheetNames?: unknown;
    tables?: unknown;
  };

  const sheetPaths = Array.isArray(objectPayload.sheetPaths)
    ? objectPayload.sheetPaths.filter((value): value is string => typeof value === "string")
    : [];
  const sheetNames = Array.isArray(objectPayload.sheetNames)
    ? objectPayload.sheetNames.filter((value): value is string => typeof value === "string")
    : [];
  const rawTables = Array.isArray(objectPayload.tables)
    ? objectPayload.tables.filter((value): value is string => typeof value === "string")
    : [];
  let tables = rawTables;
  if (tables.length === 0 && sheetNames.length > 0) {
    tables = sheetNames;
  }
  if (tables.length === 0 && sheetPaths.length > 0) {
    tables = sheetPaths;
  }

  return { tables, sheetPaths, sheetNames };
}

function extractPayloadFromObject(value: Record<string, unknown>): SourceTablesPayload {
  const directTables = getStringArray(value.tables);
  if (directTables.length > 0) return directTables;

  const nestedTables = getNestedTablesPayload(value.tables);
  if (nestedTables) return nestedTables;

  const tableNames = getStringArray(value.table_names);
  if (tableNames.length > 0) return tableNames;

  const sheetPaths = getStringArray(value.sheetPaths);
  const sheetNames = getStringArray(value.sheetNames);
  if (sheetPaths.length > 0 || sheetNames.length > 0) {
    return {
      tables: [],
      sheetPaths,
      sheetNames,
    };
  }

  return undefined;
}

function getStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function getNestedTablesPayload(value: unknown): SourceTablesPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const nested = value as Record<string, unknown>;
  const tables = getStringArray(nested.tables);
  const sheetPaths = getStringArray(nested.sheetPaths);
  const sheetNames = getStringArray(nested.sheetNames);

  if (tables.length === 0 && sheetPaths.length === 0 && sheetNames.length === 0) {
    return undefined;
  }

  return { tables, sheetPaths, sheetNames };
}

function findSourceTablesPayload(value: unknown, depth = 0): SourceTablesPayload {
  if (!value || typeof value !== "object" || depth > 5) {
    return undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const payload = findSourceTablesPayload(item, depth + 1);
      if (payload) {
        return payload;
      }
    }
    return undefined;
  }

  const objectValue = value as Record<string, unknown>;
  const directPayload = extractPayloadFromObject(objectValue);
  if (directPayload) {
    return directPayload;
  }

  for (const nestedValue of Object.values(objectValue)) {
    const nestedPayload = findSourceTablesPayload(nestedValue, depth + 1);
    if (nestedPayload) {
      return nestedPayload;
    }
  }

  return undefined;
}

export function getSourceTablesFromResponse(response: CreateSourceResponseItem): ExtractedSourceTables {
  const payload = findSourceTablesPayload(response);
  return extractSourceTables(payload);
}
